// /dashboards — overview landing.
//
// Renders a card grid of every dashboard the caller owns, plus a
// dashed "Create new dashboard" tile. Each card mirrors the v7 mockup
// (.overview-card): mini-grid preview sized by tile count
// (kind-3 / kind-4 / kind-9 / kind-16), name, "<n> cameras · <m>
// locations · Updated …" subline, and a delete button on the action
// row.
//
// The "create new" affordance hits dashboardsClient.create with a
// default name, then routes to the new dashboard's wall so the user
// can add cameras (wave 4 will own the library pane). For this wave
// the wall just renders empty + the empty state.
//
// Per-card camera/location counts come from the dashboard's tiles
// array (no second round-trip to /api/cameras). The "locations" count
// is approximated as the number of distinct tile.doorbot_id buckets
// since the dashboard wire model doesn't carry location_id today;
// wave 5 will resolve real location names off /api/cameras and
// surface them on the subline.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ApiError } from "@/api";
import { dashboardsClient } from "@/api/dashboardsClient";
import { Button, Modal, useToast } from "@/components";
import type { Dashboard, DashboardTile } from "@/types/dashboard";
import { feedGradientFor } from "@/components/cameraTileFeed";

/**
 * Pick the closest "kind-N" preview class for a tile count. The mockup
 * has only 3/4/9/16 styles; we round up so a 5-tile dashboard reads as
 * a 9-grid preview rather than a 4-grid that drops tiles.
 */
function previewKindFor(tileCount: number): "kind-3" | "kind-4" | "kind-9" | "kind-16" {
  if (tileCount <= 3) return "kind-3";
  if (tileCount <= 4) return "kind-4";
  if (tileCount <= 9) return "kind-9";
  return "kind-16";
}

/** Number of cells the "kind-N" preview renders. */
function previewCellCount(kind: ReturnType<typeof previewKindFor>): number {
  switch (kind) {
    case "kind-3":
      return 4; // 3 cells, but the hero spans 2x2 leaving 4 visual blocks
    case "kind-4":
      return 4;
    case "kind-9":
      return 9;
    case "kind-16":
      return 16;
  }
}

/** "Updated 2h ago" / "Updated yesterday" / "Updated Mar 4". */
function formatUpdated(iso: string | null | undefined): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const deltaMs = Date.now() - date.getTime();
  if (deltaMs < 0) return "Updated just now";
  const minutes = Math.floor(deltaMs / 60_000);
  if (minutes < 1) return "Updated just now";
  if (minutes < 60) return `Updated ${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Updated ${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `Updated ${days}d ago`;
  return `Updated ${date.toLocaleDateString()}`;
}

/** Distinct doorbot_id buckets — used as a stand-in for "location count"
 *  until the dashboard wire model carries location_id. */
function distinctCameras(tiles: DashboardTile[]): number {
  return new Set(tiles.map((t) => t.doorbot_id)).size;
}

export function DashboardsOverviewPage(): JSX.Element {
  const navigate = useNavigate();
  const toast = useToast();

  const [dashboards, setDashboards] = useState<Dashboard[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState<string>("");
  const [creating, setCreating] = useState<boolean>(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  // Inline name-prompt + delete-confirm modal state. Both keyed by the
  // dashboard they're acting on (delete) or empty (create).
  const [createPromptOpen, setCreatePromptOpen] = useState<boolean>(false);
  const [createName, setCreateName] = useState<string>("");
  const [deleteCandidate, setDeleteCandidate] = useState<Dashboard | null>(
    null,
  );

  const load = useCallback(async (signal?: AbortSignal): Promise<void> => {
    try {
      const list = await dashboardsClient.list();
      if (signal?.aborted) return;
      setDashboards(list);
      setLoadError(null);
    } catch (err) {
      if (signal?.aborted) return;
      const msg =
        err instanceof ApiError
          ? `${err.status} ${err.code ?? err.message}`
          : err instanceof Error
            ? err.message
            : "Failed to load dashboards";
      setLoadError(msg);
      setDashboards([]);
    }
  }, []);

  useEffect(() => {
    const ctrl = new AbortController();
    void load(ctrl.signal);
    return () => ctrl.abort();
  }, [load]);

  const filtered = useMemo<Dashboard[]>(() => {
    if (!dashboards) return [];
    const q = search.trim().toLowerCase();
    if (!q) return dashboards;
    return dashboards.filter((d) => d.display_name.toLowerCase().includes(q));
  }, [dashboards, search]);

  const handleOpenCreatePrompt = useCallback((): void => {
    setCreateName("");
    setCreatePromptOpen(true);
  }, []);

  const handleCommitCreate = useCallback(async (): Promise<void> => {
    if (creating) return;
    const name = createName.trim();
    if (name.length === 0) {
      toast.danger({
        title: "Name required",
        description: "Give your dashboard a name to create it.",
      });
      return;
    }
    setCreating(true);
    try {
      const dash = await dashboardsClient.create({
        display_name: name,
        tiles: [],
      });
      toast.success({ title: `Created "${dash.display_name}"` });
      setCreatePromptOpen(false);
      setCreateName("");
      navigate(`/dashboards/${encodeURIComponent(dash.dashboard_id)}?mode=edit`);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Failed to create dashboard";
      toast.danger({ title: "Could not create dashboard", description: message });
    } finally {
      setCreating(false);
    }
  }, [createName, creating, navigate, toast]);

  // Two-stage delete: card click stages the candidate; the confirm modal
  // is the only path to dashboardsClient.remove. No soft-delete — once
  // confirmed it's gone.
  const handleConfirmDelete = useCallback(async (): Promise<void> => {
    const dash = deleteCandidate;
    if (!dash) return;
    if (deletingId !== null) return;
    setDeletingId(dash.dashboard_id);
    try {
      await dashboardsClient.remove(dash.dashboard_id, dash.version);
      setDashboards((curr) =>
        curr ? curr.filter((d) => d.dashboard_id !== dash.dashboard_id) : curr,
      );
      toast.success({ title: `Deleted "${dash.display_name}"` });
      setDeleteCandidate(null);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Failed to delete dashboard";
      toast.danger({
        title: "Could not delete dashboard",
        description: message,
      });
    } finally {
      setDeletingId(null);
    }
  }, [deleteCandidate, deletingId, toast]);

  return (
    <div className="v7-overview">
      <div className="v7-overview__head">
        <div>
          <h1>Dashboards</h1>
          <div className="v7-overview__caption">
            Saved camera walls. Switch any dashboard from the carousel above.
          </div>
        </div>
        <div className="v7-overview__actions">
          <button
            type="button"
            className="v7-btn-primary"
            onClick={handleOpenCreatePrompt}
            disabled={creating}
          >
            + New dashboard
          </button>
        </div>
      </div>

      <div className="v7-overview-search">
        <span className="ico" aria-hidden="true">
          <svg viewBox="0 0 24 24">
            <path d="M15.5 14h-.8l-.3-.3a6.5 6.5 0 1 0-.7.7l.3.3v.8l5 5 1.5-1.5-5-5zm-6 0a4.5 4.5 0 1 1 0-9 4.5 4.5 0 0 1 0 9z" />
          </svg>
        </span>
        <input
          type="text"
          placeholder="Search dashboards…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {loadError && (
        <div className="v7-overview__error" role="alert">
          {loadError}
        </div>
      )}

      {dashboards === null && !loadError && (
        <div className="v7-overview__loading">Loading dashboards…</div>
      )}

      {dashboards !== null && (
        <div className="v7-overview-grid">
          {filtered.map((dash) => {
            const tileCount = dash.tiles.length;
            const kind = previewKindFor(tileCount);
            const cellCount = previewCellCount(kind);
            const locationsCount = distinctCameras(dash.tiles);
            const updated = formatUpdated(dash.updated_at ?? dash.created_at);
            const subParts: string[] = [
              `${tileCount} ${tileCount === 1 ? "camera" : "cameras"}`,
              `${locationsCount} ${locationsCount === 1 ? "location" : "locations"}`,
            ];
            if (updated) subParts.push(updated);
            const openDashboard = (): void =>
              navigate(`/dashboards/${encodeURIComponent(dash.dashboard_id)}`);
            return (
              // Interactive card rendered as a div (not a <button>) so the
              // nested Delete <button> is valid DOM — a button cannot contain
              // another button. role/tabIndex/onKeyDown preserve keyboard +
              // a11y semantics.
              <div
                key={dash.dashboard_id}
                role="button"
                tabIndex={0}
                className={`v7-overview-card ${kind}`}
                aria-label={`Open ${dash.display_name}`}
                onClick={openDashboard}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    openDashboard();
                  }
                }}
              >
                <div className="v7-overview-card__preview" aria-hidden="true">
                  {Array.from({ length: cellCount }).map((_, i) => {
                    // Pull a tile's doorbot_id when one exists for a slot
                    // (so a real dashboard's preview echoes its tiles);
                    // otherwise fall back to a synthetic seed off the
                    // dashboard id so empty-ish cards still get distinct
                    // cells.
                    const tile = dash.tiles[i];
                    const seed = tile?.doorbot_id ?? `${dash.dashboard_id}:${i}`;
                    return (
                      <i
                        key={i}
                        style={{ background: feedGradientFor(seed) }}
                      />
                    );
                  })}
                </div>
                <div className="v7-overview-card__meta">
                  <div className="v7-overview-card__name">
                    {dash.display_name}
                  </div>
                  <div className="v7-overview-card__sub">
                    {subParts.map((part, i) => (
                      <span key={i}>
                        {i > 0 && <span aria-hidden="true">·&nbsp;</span>}
                        {part}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="v7-overview-card__row">
                  <button
                    type="button"
                    className="v7-overview-card__action"
                    title="Delete dashboard"
                    aria-label={`Delete ${dash.display_name}`}
                    disabled={deletingId === dash.dashboard_id}
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteCandidate(dash);
                    }}
                  >
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                    </svg>
                  </button>
                </div>
              </div>
            );
          })}
          <button
            type="button"
            className="v7-overview-create-card"
            onClick={handleOpenCreatePrompt}
            disabled={creating}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6z" />
            </svg>
            <span>Create new dashboard</span>
          </button>
        </div>
      )}

      {dashboards !== null && filtered.length === 0 && search.trim() !== "" && (
        <div className="v7-overview__empty">
          No dashboards match &ldquo;{search}&rdquo;.
        </div>
      )}

      {dashboards !== null &&
        dashboards.length === 0 &&
        search.trim() === "" && (
          <div className="v7-overview__empty-cta">
            <p>No dashboards yet — create one to get started.</p>
            <button
              type="button"
              className="v7-btn-primary"
              onClick={handleOpenCreatePrompt}
              disabled={creating}
            >
              Create dashboard
            </button>
          </div>
        )}

      <Modal
        open={createPromptOpen}
        onClose={() => {
          if (!creating) setCreatePromptOpen(false);
        }}
        title="Create dashboard"
        description="Give your new camera wall a name. You can rename it later."
        variant="confirm"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setCreatePromptOpen(false)}
              disabled={creating}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => void handleCommitCreate()}
              disabled={creating || createName.trim().length === 0}
            >
              {creating ? "Creating…" : "Create"}
            </Button>
          </>
        }
      >
        <input
          type="text"
          autoFocus
          className="v7-create-input"
          placeholder="e.g. Front-of-house"
          value={createName}
          onChange={(e) => setCreateName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && createName.trim().length > 0 && !creating) {
              e.preventDefault();
              void handleCommitCreate();
            }
          }}
          aria-label="Dashboard name"
        />
      </Modal>

      <Modal
        open={deleteCandidate !== null}
        onClose={() => {
          if (deletingId === null) setDeleteCandidate(null);
        }}
        title={
          deleteCandidate
            ? `Delete "${deleteCandidate.display_name}"?`
            : "Delete dashboard?"
        }
        description="This is permanent. The dashboard layout will be removed for everyone who uses your account."
        variant="confirm"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setDeleteCandidate(null)}
              disabled={deletingId !== null}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => void handleConfirmDelete()}
              disabled={deletingId !== null}
            >
              {deletingId !== null ? "Deleting…" : "Delete dashboard"}
            </Button>
          </>
        }
      />
    </div>
  );
}
